// react
import { useContext, useEffect, useState } from "react";
// libs
import { AnimatePresence, motion, Variants } from "framer-motion";
import styled from "styled-components";
import { useQueryParam } from "../../lib/hooks/react-router";
// components
import { Book } from "../Book";
import { Box } from "../Box";
import IconButton from "../IconButton";
import { ImagePanner } from "../ImagePanner";
import { Interaction } from "../Interaction";
import { Portal } from "../Portal";
import { SliderButton } from "../SliderButton";
import { ThreeSixty } from "../three-sixty/ThreeSixty";
import { PTOverlayInfo } from "./ATOverlayInfo";
// context
import { ArtifactViewSelectionContext } from "../../context/ArtifactViewSelectionContext";
// other
import {
  Artifact,
  BookArtifact,
  ImageArtifact,
  InteractionArtifact,
  ThreeSixtyArtifact,
} from "../../models/client";
import { fill } from "../../styles/utils";

interface ATOverlayProps {
  artifacts: Artifact[];
}

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 3000;
`;

const Background = styled(motion.div)`
  ${fill()}
  background-color: white;
  z-index: -1;
`;

const CloseButtonContainer = styled(motion.div)`
  position: absolute;
  top: 24px;
  right: 32px;
  z-index: 10;
`;

const AnimationParent = styled(motion.div)`
  ${fill()}
`;

const ContentContainer = styled.div`
  position: relative;
  padding: 16px 64px;
  height: 100%;
`;

const Content = styled(motion.div)`
  position: relative;
  /* background-color: beige; */
  height: 100%;
`;

const wrapperVariants: Variants = {
  initial: { opacity: 0 },
  enter: { opacity: 1, transition: { duration: 0.4, type: "tween" } },
  exit: {
    opacity: 0,
    transition: { duration: 0.4, type: "tween", delay: 0.4 },
  },
};

const otherVariants: Variants = {
  initial: { opacity: 0 },
  enter: {
    opacity: 1,
    transition: { duration: 0.4, type: "tween", delay: 0.4 },
  },
  exit: { opacity: 0, transition: { duration: 0.4, type: "tween" } },
};

const getContent = (selectedArtifact: Artifact): JSX.Element => {
  if (selectedArtifact.type === "photoArtifact") {
    const imageArtifact = selectedArtifact as ImageArtifact;
    return <ImagePanner src={imageArtifact.fullImg.src} />;
  }
  if (selectedArtifact.type === "bookArtifact") {
    const { pages, height, width, hasCover } = selectedArtifact as BookArtifact;
    return (
      <Book pages={pages} height={height} width={width} hasCover={hasCover} />
    );
  }
  if (selectedArtifact.type === "threeSixtyArtifact") {
    const { threeSixtyImages } = selectedArtifact as ThreeSixtyArtifact;
    return <ThreeSixty images={threeSixtyImages} />;
  }
  if (selectedArtifact.type === "interactiveArtifact") {
    const artifact = selectedArtifact as InteractionArtifact;
    return <Interaction artifact={artifact} />;
  }
  return <Box></Box>;
};

function ATOverlay({ artifacts }: ATOverlayProps) {
  const { selectedArtifact, setArtifactById } = useContext(
    ArtifactViewSelectionContext
  );

  const [artifactIdParam] = useQueryParam("a");

  const [infoOpen, setInfoOpen] = useState(false);

  const handleNextClick = (): void => {
    if (!selectedArtifact) {
      return;
    }
    const currentIndex = artifacts.findIndex(
      (a) => a.id === selectedArtifact.id
    );
    if (artifacts.length - 1 === currentIndex) {
      setArtifactById(artifacts[0].id);
    } else {
      setArtifactById(artifacts[currentIndex + 1].id);
    }
  };

  const handlePrevClick = (): void => {
    if (!selectedArtifact) {
      return;
    }
    const currentIndex = artifacts.findIndex(
      (a) => a.id === selectedArtifact.id
    );
    if (0 === currentIndex) {
      setArtifactById(artifacts[artifacts.length - 1].id);
    } else {
      setArtifactById(artifacts[currentIndex - 1].id);
    }
  };

  const handleInfoToggleClick = (): void => {
    setInfoOpen(!infoOpen);
  };

  useEffect(() => {
    if (artifacts && artifactIdParam) {
      const timer = setTimeout(() => {
        setArtifactById(Number(artifactIdParam));
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [artifactIdParam, artifacts, setArtifactById]);

  return (
    <Portal>
      <AnimatePresence>
        {selectedArtifact ? (
          <Wrapper>
            <AnimationParent initial="initial" animate="enter" exit="exit">
              <Background variants={wrapperVariants} />
              {/* <Blur variants={blurVariants} /> */}
              <CloseButtonContainer variants={otherVariants}>
                <IconButton
                  icon="close"
                  onClick={() => setArtifactById(null)}
                />
              </CloseButtonContainer>

              <ContentContainer>
                <Content variants={otherVariants}>
                  <AnimatePresence exitBeforeEnter>
                    <Content
                      key={selectedArtifact.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.6 }}
                    >
                      {getContent(selectedArtifact)}
                      <PTOverlayInfo
                        isOpen={infoOpen}
                        onToggleClick={handleInfoToggleClick}
                        heading={selectedArtifact.name}
                        copy={selectedArtifact.summary}
                      />
                    </Content>
                  </AnimatePresence>
                </Content>
              </ContentContainer>
              <motion.div variants={otherVariants}>
                <SliderButton direction="prev" onClick={handlePrevClick}>
                  Previous
                </SliderButton>
                <SliderButton direction="next" onClick={handleNextClick}>
                  Next
                </SliderButton>
              </motion.div>
            </AnimationParent>
          </Wrapper>
        ) : null}
      </AnimatePresence>
    </Portal>
  );
}

export default ATOverlay;
